import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import {
  NavigationEnd,
  Params,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import { IFilter } from '@epicuro-next/ataraxia/models';
import { PopoverDirective } from '@epicuro-next/ataraxia/popover';
import { BasketItemSource } from '@epicuro-next/models/basket';
import { RecommendationsPanels } from '@epicuro-next/models/recommendation-panels';
import { RecentProductState } from '@epicuro-next/navigation/main-navigation';
import {
  selectBookmarkedIcon,
  selectQuickSearchDropdown,
  selectSearchNewsConfigRule,
} from '@epicuro-next/platform/configuration';
import { DynamicLocalePipe } from '@epicuro-next/platform/i18n';
import { RecommendationPanelsProviderService } from '@epicuro-next/recommendation-panels';
import {
  activateConfigStrip,
  ConfigStripActivationSource,
  selectIsConfigStripOpen,
  setProductSource,
} from '@epicuro-next/state/config-strip-state';
import {
  clearFilters,
  selectPresetDisplayName,
  selectProductBasketQueryParams,
  selectProductBasketSearchQuery,
  selectProducts,
  toggleFilter,
} from '@epicuro-next/state/p-basket-state';
import { go, selectQueryParams } from '@epicuro-next/state/router-store';
import {
  selectSiteId,
  selectSubSiteId,
} from '@epicuro-next/state/session-state';
import { recommendationItemClicked } from '@epicuro-next/state/tracking';
import { setAsideMenuOpenedState } from '@epicuro-next/state/user-ui-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { TypewriterService } from 'ataraxia-type-writer';
import { Product } from 'graphql-api/gql-api-interfaces';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subject,
  timer,
} from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { isPBasket } from '../is-page';

import { SearchBarComponentStore } from './search-bar.component-store';

export enum SearchBarPaddingTypes {
  Large = 170,
  Middle = 155,
  Default = 115,
}

const searchPlaceholderLabel = $localize`:searchBar|Placeholder for search bar@@searchBarPlaceholder:Search, press ENTER`;

const CLOSE_WITH_TIMER_DELAY = 100;

@Component({
  selector: 'epc-search-bar',
  templateUrl: './search-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./search-bar.component.scss'],
  providers: [SearchBarComponentStore, TypewriterService, DynamicLocalePipe],
  standalone: false,
})
export class SearchBarComponent implements OnDestroy, OnInit {
  @ViewChild(PopoverDirective, { static: false })
  public popover: PopoverDirective;

  private readonly typewriterService = inject(TypewriterService);
  private readonly localePipe = inject(DynamicLocalePipe);

  public bookmarkIcon$ = this.store.pipe(select(selectBookmarkedIcon));
  public products$ = this.store.pipe(select(selectProducts));
  public siteId$ = this.store.pipe(select(selectSiteId));
  public subSiteId$ = this.store.pipe(select(selectSubSiteId));

  public quickSearchEnabled$ = this.store.pipe(
    select(selectQuickSearchDropdown),
  );

  public isMobile$ = atxIsMobile$;
  public isPBasket$ = new BehaviorSubject(false);
  public searchHistory$ = this.componentStore.searchHistory$.pipe(
    withLatestFrom(this.siteId$, this.subSiteId$),
    filter(([searchHistory, siteId]) => !!searchHistory && !!siteId),
    map(([searchHistory, siteId, subSiteId]) => {
      const key = this.componentStore.getSiteKey(siteId, subSiteId);
      return searchHistory[key];
    }),
  );
  public recentProducts$ = this.componentStore.recentProducts$;
  public inputInFocus$ = new BehaviorSubject(false);

  public quickSearchProducts$ = this.componentStore.products$;
  public hasQuickSearchProducts$ = this.quickSearchProducts$.pipe(
    map((products) => products.length && this.searchInputHasValue),
  );
  public didQuickSearch$ = this.componentStore.didQuickSearch$;
  public isLoading$ = this.componentStore.isLoading$;
  public error$ = this.componentStore.error$;
  public readonly isSearchSuggestEnabled$ =
    this.componentStore.isSearchSuggestEnabled$;
  public readonly searchSuggestResult = toSignal(
    this.componentStore.searchSuggestResult$,
  );

  private readonly searchNewsConfig = this.store.pipe(
    select(selectSearchNewsConfigRule),
  );

  public searchPlaceholder$ = this.searchNewsConfig.pipe(
    switchMap((config) => {
      if (config.titles?.length) {
        const localedTitles = config.titles.map((title) =>
          this.localePipe.transform(title),
        );

        const titles = config.altWithDefault
          ? localedTitles.flatMap((title) => [searchPlaceholderLabel, title])
          : [searchPlaceholderLabel, ...localedTitles];

        return this.typewriterService.loopWriterEffect(titles, {
          ...{
            writeSpeed: 100,
            writeDeleteSpeed: 50,
            waitAfterTyped: 3000,
          },
          ...config.options,
        });
      }

      return of(searchPlaceholderLabel);
    }),
  );

  public hasAnySuggestions = computed(
    () =>
      !!this.searchSuggestResult()?.suggestions?.length ||
      !!this.searchSuggestResult()?.totalResultCount,
  );
  public readonly searchSuggestFilters = computed(
    () =>
      this.searchSuggestResult()?.suggestions?.filter((s) => s.isFilter) ?? [],
  );
  public readonly searchSuggestAutocomplete = computed(
    () =>
      this.searchSuggestResult()?.suggestions?.filter((s) => !s.isFilter) ?? [],
  );

  /** Destroy Subject */
  private destroy$: Subject<void> = new Subject();

  public isNotMobile$ = atxIsMobile$.pipe(map((isMobile) => !isMobile));

  public displayBrowseMenuButton$$ = combineLatest([
    this.isPBasket$,
    this.isNotMobile$,
  ]).pipe(map(([isThatPbasket, isNotMobile]) => isThatPbasket && isNotMobile));

  /**
   * Input form control
   */
  public searchInputControl = new FormControl<string>('');

  public get searchInputHasValue() {
    return !!this.searchInputControl.value;
  }

  public recommendationsPanels = RecommendationsPanels;

  public isSmallPresetName$ = this.store
    .pipe(select(selectPresetDisplayName))
    .pipe(map((name) => (name ?? '').length < 5));

  public isShowSearchBarPanel$: Observable<boolean> =
    this.panelsProviderService.recommendationsPanelsItems$.pipe(
      switchMap((panels) =>
        of(panels[RecommendationsPanels.SearchBar].length > 0),
      ),
    );

  public isShowSearchSuggestBarPanel$: Observable<boolean> =
    this.panelsProviderService.recommendationsPanelsItems$.pipe(
      switchMap((panels) =>
        of(panels[RecommendationsPanels.SearchSuggestBar].length > 0),
      ),
    );

  public paddingRight = SearchBarPaddingTypes.Default;

  private triggerSingleProductOpenCS$ = new Subject<void>();

  private readonly resetOtherFiltersOnSuggest$ =
    this.componentStore.resetOtherFiltersOnSuggest$;

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly componentStore: SearchBarComponentStore,
    private readonly panelsProviderService: RecommendationPanelsProviderService,
  ) {}

  public ngOnInit() {
    combineLatest([this.isSmallPresetName$, this.inputInFocus$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isSmallPresetName, inputInFocus]) => {
        if (inputInFocus) {
          this.paddingRight = SearchBarPaddingTypes.Large;
        } else {
          this.paddingRight = isSmallPresetName
            ? SearchBarPaddingTypes.Default
            : SearchBarPaddingTypes.Middle;
        }
      });

    this.isPBasket$.next(isPBasket(this.router.url));

    this.subscribeSingleProductOpenCS();

    this.subscribeRouteChangeEvent();

    this.componentStore.query$
      .pipe(takeUntil(this.destroy$))
      .subscribe((query) => {
        this.searchInputControl.setValue(query, { emitEvent: false });
      });

    this.subscribeToValueChanges();
  }

  public findRecentProduct(
    recentProducts: RecentProductState[],
    historyTerm: string,
  ): RecentProductState | undefined {
    return recentProducts.find(
      (item) =>
        item.articleId === historyTerm.replace('-', '') ||
        item.articleId === historyTerm ||
        item.styleId === historyTerm,
    );
  }

  /**
   * Subscription cleanup
   */
  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * method to set the search query
   */
  public search(value?: string) {
    this.closeWithTimer();
    this.componentStore.query$
      .pipe(
        withLatestFrom(
          this.store.pipe(select(selectProductBasketQueryParams)),
          this.store.pipe(select(selectIsConfigStripOpen)),
        ),
        take(1),
      )
      .subscribe(([prevQuery, pBasketParams, isCSOpen]) => {
        const query =
          value === undefined
            ? (this.searchInputControl.value as string)
            : value;

        this.componentStore.setQuery(query);

        if (!query || prevQuery !== query || isCSOpen) {
          this.redirectToSearchResults(query, pBasketParams);
        } else if (
          query &&
          prevQuery === query &&
          !this.searchInputControl.value
        ) {
          // update input when user clicks on the same query from history list after reset
          this.searchInputControl.setValue(query);
        }

        // click on search history item which equal current search should emit check for open single product feature
        if (query && prevQuery === query) {
          this.triggerSingleProductOpenCS$.next();
        }
      });
  }
  public toggleAsideMenu(value: boolean): void {
    this.store.dispatch(setAsideMenuOpenedState({ opened: value }));
  }

  public async redirectToSearchResults(query: string, params: Params) {
    this.store.dispatch(setAsideMenuOpenedState({ opened: false }));

    // close all outlets. specifically CS overlay
    await this.router.navigate([{ outlets: { overlay: null } }], {
      queryParamsHandling: 'preserve',
    });

    this.store.dispatch(
      go({
        commands: ['/', 'browse', 'search'],
        extras: {
          // override with full p-basket params
          queryParams: {
            ...params,
            q: query ?? '',
          },
          queryParamsHandling: '',
        },
      }),
    );
  }

  public closeWithTimer() {
    timer(CLOSE_WITH_TIMER_DELAY)
      .pipe(take(1))
      .subscribe(() => {
        this.popover.close();
        this.inputInFocus$.next(false);
        this.componentStore.patchState({ didQuickSearch: false });
      });
  }

  public focusInput() {
    this.inputInFocus$.next(true);
    if (this.searchInputControl.value) {
      this.componentStore.loadSuggestProducts(this.searchInputControl.value);
    }
  }

  public openSearchPopover() {
    this.inputInFocus$
      .pipe(
        filter((value) => !value),
        take(1),
      )
      .subscribe(() => {
        this.popover.open();
        this.focusInput();
      });
  }

  public onPopoverClosed() {
    this.inputInFocus$.next(false);
  }

  public removeFromHistory(text: string) {
    this.componentStore.removeFromHistory(text);
  }

  public resetSearch() {
    this.searchInputControl.setValue('');
    this.openSearchPopover();
    this.componentStore.patchState({
      didQuickSearch: false,
      searchSuggestResult: null,
    });
  }

  public selectProduct() {
    setTimeout(() => {
      this.store.dispatch(
        recommendationItemClicked({
          correlationId: '0',
          providerId: 'ProductSearch',
          servingConfig: 'ProductSearch',
          panelName: 'QuickSearchDropdown',
          hash: this.searchInputControl.value ?? '',
          panelStyle: 'QSD',
        }),
      );
    }, 1500);
  }

  public onAutocompleteItemClick(query: string) {
    this.resetOtherFiltersOnSuggest$
      .pipe(
        take(1),
        tap((resetOtherFilters) => {
          if (resetOtherFilters) {
            this.store.dispatch(
              clearFilters({
                keepPreset: true,
                skipParams: false,
              }),
            );
          }
        }),
        // wait until query is updated after clearFilters
        debounceTime(500),
        concatLatestFrom(() => this.store.select(selectQueryParams)),
        tap(([_, params]) => {
          this.store.dispatch(
            setProductSource({
              source: BasketItemSource.SEARCH_SUGGEST_AUTOCOMPLETE,
              details: {
                query: this.searchInputControl.value ?? query,
              },
              additionalUiDetails: {
                searchParams: params,
                query,
              },
            }),
          );
          this.search(query);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public onFilterItemClick(filterItemClickEvent: {
    filter: IFilter;
    isActive: boolean;
  }) {
    const query = this.searchInputControl.value ?? '';
    this.searchInputControl.setValue('', { emitEvent: false });

    this.resetOtherFiltersOnSuggest$
      .pipe(
        take(1),
        filter(
          (resetOtherFilters) =>
            resetOtherFilters || !filterItemClickEvent.isActive,
        ),
        tap((resetOtherFilters) => {
          this.store.dispatch(
            toggleFilter({
              fieldId: filterItemClickEvent.filter.fieldId,
              value: filterItemClickEvent.filter.value,
              resetOtherFilters,
            }),
          );
        }),
        // wait until query is updated after toggleFilter
        debounceTime(500),
        concatLatestFrom(() => this.store.select(selectQueryParams)),
        tap(([_, params]) => {
          this.store.dispatch(
            setProductSource({
              source: BasketItemSource.SEARCH_SUGGEST_FILTER,
              details: {
                query,
                suggestedFilter: {
                  fieldId: filterItemClickEvent.filter.fieldId,
                  value: filterItemClickEvent.filter.value,
                },
              },
              additionalUiDetails: {
                searchParams: params,
              },
            }),
          );

          this.search();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public onSuggestProductClick(product: Product) {
    this.store.dispatch(
      activateConfigStrip({
        product,
        index: 1,
        source: {
          source: ConfigStripActivationSource.Search,
          subSource: BasketItemSource.SEARCH_SUGGEST_PRODUCT,
          details: {
            query: this.searchInputControl.value ?? '',
          },
        },
        setSourceNeeded: true,
      }),
    );
  }
  /** Router change event listener */
  private subscribeRouteChangeEvent() {
    this.router.events
      .pipe(
        filter((event: RouteConfigLoadStart) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.isPBasket$.next(isPBasket(this.router.url)));
  }

  private subscribeSingleProductOpenCS() {
    combineLatest([
      this.products$,
      this.triggerSingleProductOpenCS$.pipe(startWith(null)),
    ])
      .pipe(
        map(([products]) => products),
        withLatestFrom(
          this.store.pipe(select(selectProductBasketSearchQuery)),
          this.isPBasket$,
        ),
        filter(([products, search, isPbasket]) => {
          return (
            isPbasket &&
            isNonNullish(search) &&
            search.length > 0 &&
            products.length === 1
          );
        }),
        tap(([[singleProduct], search]) => {
          this.componentStore.writeRecentProduct(singleProduct);

          this.store.dispatch(
            activateConfigStrip({
              product: singleProduct,
              index: 1,
              source: {
                source: ConfigStripActivationSource.Search,
                details: {
                  query: search ?? '',
                },
              },
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private subscribeToValueChanges() {
    // open popover if closed
    this.searchInputControl.valueChanges
      .pipe(
        // open delay should be less than close delay, as it triggers opening popover after closing if User clicks quickly after changing query
        debounceTime(CLOSE_WITH_TIMER_DELAY - 10),
        filter((query) => !!query?.length && !this.popover.opened),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.popover.open();
      });

    const valueChanges = this.searchInputControl.valueChanges.pipe(
      debounceTime(1000),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    // load products if search suggest is not enabled
    valueChanges
      .pipe(
        concatLatestFrom(() => [
          this.quickSearchEnabled$,
          this.isSearchSuggestEnabled$,
        ]),
        filter(([query, quickSearchEnabled, searchSuggestEnabled]) =>
          Boolean(query?.length && quickSearchEnabled && !searchSuggestEnabled),
        ),
        map(([query]) => query),
        tap((query: string) => {
          this.componentStore.loadProducts(query);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    // subscribe to search suggest
    valueChanges
      .pipe(
        concatLatestFrom(() => [this.isSearchSuggestEnabled$]),
        filter(([_, searchSuggestEnabled]) => !!searchSuggestEnabled),
        map(([query]) => query),
        tap((query: string) => {
          this.componentStore.loadSuggestProducts(query);

          if (!query) {
            this.componentStore.patchState({
              searchSuggestResult: null,
            });
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
